import React from "react";
import { Link } from "react-router-dom";

import Icon from "./icons";
import styles from "./Topbar.module.scss";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface TopbarProps {
  breadcrumb: BreadcrumbItem[];
  /** Toggles the sidebar drawer on mobile. */
  onMenuToggle: () => void;
  /** Right-side actions. When omitted, the default Reload/Updated/Notifications cluster renders. */
  actions?: React.ReactNode;
  /** Default-cluster helpers (ignored when `actions` is supplied). */
  updatedLabel?: string;
  onReload?: () => void;
  onNotifications?: () => void;
  notificationCount?: number;
  /** A live notifications control. Replaces the default bell, and also shows beside custom `actions`. */
  bell?: React.ReactNode;
}

const Crumbs = ({ items }: { items: BreadcrumbItem[] }) => (
  <div className={styles.breadcrumb}>
    <Link to="/dashboard" className={styles.breadcrumbHome} aria-label="Dashboard">
      <Icon name="dashboard-filled" size={16} />
    </Link>
    {items.map((item, i) => (
      <React.Fragment key={`${item.label}-${i}`}>
        {i > 0 && <span className={styles.breadcrumbSep} aria-hidden="true" />}
        {item.to && i < items.length - 1 ? (
          <Link to={item.to} className={styles.breadcrumbLink}>
            {item.label}
          </Link>
        ) : (
          <span
            className={`${styles.breadcrumbItem}${i === items.length - 1 ? ` ${styles.breadcrumbCurrent}` : ""}`}
            aria-current={i === items.length - 1 ? "page" : undefined}
          >
            {item.label}
          </span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const DefaultActions = ({
  updatedLabel,
  onReload,
  onNotifications,
  notificationCount,
  bell,
}: Pick<TopbarProps, "updatedLabel" | "onReload" | "onNotifications" | "notificationCount" | "bell">) => (
  <>
    <button type="button" className={styles.action} onClick={onReload}>
      <Icon name="reload" size={16} />
      <span className={styles.actionLabel}>Reload</span>
    </button>
    {updatedLabel && (
      <span className={styles.updated}>
        <Icon name="clock" size={16} />
        <span className={styles.actionLabel}>{updatedLabel}</span>
      </span>
    )}
    {bell ?? (
      <button type="button" className={styles.action} onClick={onNotifications}>
        <span className={styles.bellWrap}>
          <Icon name="bell" size={16} />
          {notificationCount ? (
            <span className={styles.bellBadge}>{notificationCount > 9 ? "9+" : notificationCount}</span>
          ) : null}
        </span>
        <span className={styles.actionLabel}>Notifications</span>
      </button>
    )}
  </>
);

const Topbar = ({
  breadcrumb,
  onMenuToggle,
  actions,
  updatedLabel,
  onReload,
  onNotifications,
  notificationCount,
  bell,
}: TopbarProps) => (
  <header className={styles.topbar}>
    <div className={styles.left}>
      <button
        type="button"
        className={styles.menuToggle}
        onClick={onMenuToggle}
        aria-label="Toggle menu"
      >
        <Icon name="menu" size={18} />
      </button>
      <Crumbs items={breadcrumb} />
    </div>

    <div className={styles.right}>
      {actions ? (
        <>
          {actions}
          {bell}
        </>
      ) : (
        <DefaultActions
          updatedLabel={updatedLabel}
          onReload={onReload}
          onNotifications={onNotifications}
          notificationCount={notificationCount}
          bell={bell}
        />
      )}
    </div>
  </header>
);

export default Topbar;
